import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getSubscription,
  cancelSubscription,
  reactivateSubscription,
  createCheckoutSession,
  createPortalSession,
  type SubscriptionData,
  type BillingCycle,
  type CheckoutConsent,
} from '@/services/billing-api'
import { isTransientQueryError } from '@/lib/query-retry'

export const SUBSCRIPTION_KEY = ['billing', 'subscription'] as const

/**
 * Hata durumunda otomatik yeniden deneme aralığı (#492).
 * Sağlayıcı kesintisinde (5xx) entitlement "bilinmiyor" durumuna düşer ve kullanıcıya
 * "otomatik yeniden deneniyor" denir. Bu söz ancak gerçekten periyodik refetch varsa
 * doğrudur: normalde TanStack Query hata sonrası yalnız pencere odağında tekrar dener,
 * yani sekmeden ayrılmayan kullanıcı kesinti bitse bile kilitli kalırdı.
 * Yalnız hata varken çalışır; başarılı cevapta polling durur (gereksiz yük yok).
 */
export const SUBSCRIPTION_ERROR_REFETCH_MS = 15_000

/**
 * Sabit 15 sn'lik aralık, kesintide TÜM sekmeleri aynı ritme kilitler: backend
 * toparlanmaya çalışırken istekler senkron topaklar hâlinde gelir ve bu, uygulama
 * limiter'ını tetikleyip geçici kesintiyi 429'a çevirebilir (#492 review). ±%25
 * dağıtım (jitter) topakları çözer; ortalama yük değişmez.
 */
export function subscriptionRetryDelayMs(): number {
  const jitter = 0.75 + Math.random() * 0.5
  return Math.round(SUBSCRIPTION_ERROR_REFETCH_MS * jitter)
}

/**
 * Son başarılı billing cevabına ne kadar süre entitlement kanıtı olarak güvenilir (#492).
 * Kesintide son bilinen plan korunur, ama SÜRESİZ değil: admin güveniyle aynı gerekçe ve
 * aynı süre (24 saat). TTL dolduğunda entitlement "bilinmiyor" durumuna düşer; böylece
 * aboneliği gerçekten biten bir kullanıcı, sekmesini açık bıraktığı için sınırsız Pro
 * erişimi görmez.
 */
export const SUBSCRIPTION_TRUST_TTL_MS = 24 * 60 * 60 * 1000

export function useSubscription(options?: { enabled?: boolean; userId?: string | null }) {
  const userId = options?.userId ?? null

  return useQuery<SubscriptionData>({
    queryKey: [...SUBSCRIPTION_KEY, userId],
    queryFn: getSubscription,
    staleTime: 30_000, // 30 seconds — admin/Stripe changes reflected quickly
    refetchOnWindowFocus: true, // override global false; refetch when user returns to tab
    // enabled guard: oturum yokken çağırma — login sayfasında gereksiz 401 üretmesin.
    // retry global QueryClient'tan gelir (4xx'leri retry'lamaz).
    enabled: (options?.enabled ?? true) && !!userId,
    refetchInterval: (query) => (
      query.state.error && isTransientQueryError(query.state.error)
        ? subscriptionRetryDelayMs()
        : false
    ),
  })
}

export function useCancelSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: cancelSubscription,
    onSuccess: () => qc.invalidateQueries({ queryKey: SUBSCRIPTION_KEY }),
  })
}

export function useReactivateSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: reactivateSubscription,
    onSuccess: () => qc.invalidateQueries({ queryKey: SUBSCRIPTION_KEY }),
  })
}

export function useCreateCheckout() {
  return useMutation({
    mutationFn: (vars: BillingCycle | { billingCycle: BillingCycle; consent: CheckoutConsent }) =>
      typeof vars === 'string'
        ? createCheckoutSession(vars)
        : createCheckoutSession(vars.billingCycle, vars.consent),
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url
    },
  })
}

export function useCreatePortal() {
  return useMutation({
    mutationFn: createPortalSession,
    onSuccess: (data) => {
      // Redirect to Stripe Customer Portal
      window.location.href = data.portal_url
    },
  })
}
